import React, { useEffect, useState } from 'react';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import { useApiClient, useToastService } from '@@common';
import { DialogHeaderActions } from '@@components';

import { ValidationError, getFieldValidationErrors, getValidationErrorMessage, parseValidationError } from '../../api/ApiValidationUtils';
import { ContextClient, ContextScopeModel } from '../../api/api';

interface ScopeDialogProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	contextId: string;
	scope?: ContextScopeModel; // Optional - if provided, it's edit mode
	onSuccess: () => void;
}

export default function ScopeDialog({ open, setOpen, contextId, scope, onSuccess }: ScopeDialogProps) {
	const client = useApiClient(ContextClient);
	const toastService = useToastService();
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [loading, setLoading] = useState(false);
	const [validationErrors, setValidationErrors] = useState<ValidationError | null>(null);
	const [fieldError, setFieldError] = useState<string>('');
	const [isMaximized, setIsMaximized] = useState(false);

	const isEditMode = !!scope;

	useEffect(() => {
		if (isEditMode && scope) {
			setName(scope.name);
			setDescription(scope.description || '');
		} else {
			setName('');
			setDescription('');
		}
	}, [scope, isEditMode]);

	const handleClose = () => {
		setOpen(false);
		setIsMaximized(false);
		if (isEditMode && scope) {
			setName(scope.name);
			setDescription(scope.description || '');
		} else {
			setName('');
			setDescription('');
		}
		setValidationErrors(null);
		setFieldError('');
	};

	const hasChanges = () => {
		if (!isEditMode) return name.trim().length > 0;
		if (!scope) return false;
		return name.trim() !== scope.name || description.trim() !== (scope.description || '');
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;

		if (isEditMode && !hasChanges()) {
			handleClose();
			return;
		}

		// Clear previous errors
		setValidationErrors(null);
		setFieldError('');

		setLoading(true);
		try {
			if (isEditMode && scope) {
				await client.updateContextScope(contextId, scope.id, {
					name: name.trim(),
					description: description.trim(),
				});
				toastService.success(`Scope "${scope.name}" updated successfully.`);
			} else {
				await client.addContextScope(contextId, {
					name: name.trim(),
					description: description.trim(),
				});
				toastService.success(`Scope "${name.trim()}" created successfully.`);
			}

			onSuccess();
			handleClose();
		} catch (error) {
			console.error(`Error ${isEditMode ? 'updating' : 'creating'} scope:`, error);

			const validationError = parseValidationError(error);
			if (validationError) {
				setValidationErrors(validationError);

				// Set field-specific error if available
				const fieldErrors = getFieldValidationErrors(validationError, 'ScopeName');
				if (fieldErrors.length > 0) {
					setFieldError(fieldErrors[0]);
				}

				// Show user-friendly toast message - prioritize field-specific error
				const userMessage = getValidationErrorMessage(validationError, 'ScopeName');
				toastService.error(userMessage);
			} else {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
				toastService.error(`Failed to ${isEditMode ? 'update' : 'create'} scope: ${errorMessage}`);
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMaximized}>
			<form onSubmit={handleSubmit}>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<span style={{ flex: 1 }}>{isEditMode ? 'Edit Scope' : 'Create New Scope'}</span>
					<DialogHeaderActions
						isMaximized={isMaximized}
						onToggleMaximized={() => setIsMaximized((value) => !value)}
						onClose={handleClose}
						closeDisabled={loading}
					/>
				</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						{validationErrors && <Alert severity="error">{validationErrors.detail || 'Validation failed. Please check your input.'}</Alert>}
						<TextField
							label="Scope Name"
							value={name}
							onChange={(e) => {
								setName(e.target.value);
								// Clear field error when user starts typing
								if (fieldError) setFieldError('');
								if (validationErrors) setValidationErrors(null);
							}}
							required
							fullWidth
							autoFocus
							placeholder="Enter scope name"
							error={!!fieldError}
							helperText={fieldError || 'Scope name must start with a letter and contain only letters, numbers, and dots.'}
						/>
						<TextField
							label="Description"
							value={description}
							onChange={(e) => {
								setDescription(e.target.value);
								// Clear validation errors when user starts typing
								if (validationErrors) setValidationErrors(null);
							}}
							fullWidth
							multiline
							rows={3}
							placeholder="Enter scope description (optional)"
							helperText="Provide a brief description of what this scope covers"
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button type="submit" variant="contained" disabled={loading || !name.trim() || (isEditMode && !hasChanges())}>
						{loading ? (isEditMode ? 'Updating...' : 'Creating...') : isEditMode ? 'Update' : 'Create'}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
